import React from "react"
import { useAlgoliaSerpDataContext } from "../../context/data"
import { ParsedUrlQuery } from "querystring"
import { SerpUrlBuilder } from "../../state/url"
import {
  parseHashParams,
  parseSearchOptions,
  SearchOptions,
} from "../../state/hashParse"
import { useRouter } from "next/router"
import { AlgoliaSearchStateContext } from "./state"
import { AlgoliaSearchResults, AlgoliaUiFilters } from "./state/types"
import { getDefaultSortBy, sortBy } from "../../data/sorting"
import { createAlgoliaUiFilters } from "./state/converters"
import { search } from "./state/search"
import { trackSearch } from "../../../../features/tracking"
import { SearchParameters } from "../../../../features/algolia/api/types/search"
import { SearchState } from "../../types"
import { ProductCategoryMappingsDict } from "../../../../types/serp"

interface Props {
  children: any
  initialFilters: SearchParameters
  hiddenFilters: SearchParameters
  customCategories?: ProductCategoryMappingsDict
}

const mergeSearchParams = (
  state: SearchState,
  otherState: SearchState
): SearchState => ({
  ...state,
  ...otherState,
})

const createURL = (state: SearchState, currentParams: ParsedUrlQuery) => {
  return new SerpUrlBuilder().createSerpUrl(state, currentParams)
}

const createDefaultSearchState = (): SearchState => ({
  orderBy: getDefaultSortBy() ?? "weight",
  page: 1,
})

interface AlgoliaDataState {
  initialized: boolean
  searchState: SearchState
  searchOptions: SearchOptions
  searchResults?: AlgoliaSearchResults
  uiFilters?: AlgoliaUiFilters
  hiddenFilters?: SearchParameters
  queryID?: string
}

const createInitialAlgoliaDataState = (
  hiddenFilters: SearchParameters
): AlgoliaDataState => ({
  initialized: false,
  searchOptions: {
    includeUnavailable: false,
    showEanCode: false,
  },
  searchState: createDefaultSearchState(),
  hiddenFilters,
})

export const AlgoliaSearchController = ({
  children,
  initialFilters,
  hiddenFilters,
  customCategories,
}: Props) => {
  const serpDataContext = useAlgoliaSerpDataContext()

  const [state, setState] = React.useState<AlgoliaDataState>(
    createInitialAlgoliaDataState(hiddenFilters)
  )

  const router = useRouter()
  const updateUrlSearchParams = (newSearchState: SearchState) => {
    router.push(
      `${window.location.pathname}${window.location.search}${createURL(
        newSearchState,
        router.query
      )}`,
      undefined,
      {
        shallow: true,
      }
    )
  }

  const invokeSearch = async (searchParams: SearchState) => {
    const response = await search({
      initialFilters,
      searchParams,
      settings: serpDataContext.settings,
      analytics: true,
      timestamp: new Date(),
    })
    const uiFilters = createAlgoliaUiFilters({
      searchResponses: response.results,
      serpData: serpDataContext,
      searchParams,
      customCategories,
    })
    setState({
      ...state,
      searchState: searchParams,
      uiFilters,
      searchResults: response.results[0],
      queryID: response.results[0]?.queryID,
      hiddenFilters,
    })
  }

  const initializeSearchState = () => {
    const stateFromQuery = parseHashParams()
    const initialState = mergeSearchParams(
      createDefaultSearchState(),
      stateFromQuery
    )
    const initialSearchOptions = parseSearchOptions(router.query)
    setState({
      ...state,
      searchState: initialState,
      searchOptions: initialSearchOptions,
      initialized: true,
      hiddenFilters,
    })
    invokeSearch(initialState)
  }

  React.useEffect(() => {
    trackSearch()
    initializeSearchState()
  }, [initialFilters])

  const handleSearchParamUpdate = (
    transform: (currentState: SearchState) => SearchState,
    currentState: SearchState
  ) => {
    const newState = transform(currentState)
    newState.page = 1
    invokeSearch(newState)
    updateUrlSearchParams(newState)
  }

  const handleSearchPageChange = (newIndex: number) => {
    const newState = {
      ...state.searchState,
      page: newIndex,
    }
    invokeSearch(newState)
    updateUrlSearchParams(newState)
  }

  const clearFilters = () => {
    handleSearchParamUpdate(
      (x) => ({
        orderBy: x.orderBy,
      }),
      state.searchState
    )
  }

  React.useEffect(() => {
    if (state.initialized) {
      return
    }

    initializeSearchState()
  }, [])

  return (
    <AlgoliaSearchStateContext.Provider
      value={{
        initialized: state.initialized,
        searchParams: state.searchState,
        searchResults: state.searchResults,
        orderByOptions: sortBy,
        uiFilters: state.uiFilters,
        updateSearchParams: (x) =>
          handleSearchParamUpdate(x, state.searchState),
        updateSearchPage: handleSearchPageChange,
        queryID: state.queryID,
        hiddenFilters: state.hiddenFilters,
        clearFilters: () => clearFilters(),
      }}
    >
      {children}
    </AlgoliaSearchStateContext.Provider>
  )
}
